import {
    BRIDGE_VERSION,
    nextRequestId,
    parseEnvelope,
    type Envelope,
} from './protocol';
import type { NativeApi, WebApi } from './contract';

/**
 * Web-side singleton. Loaded once per page (auto-installs `window.__zbReceive`
 * the first time this module is imported). Designed to be safe in non-RN
 * browsers too — `callNative()` simply rejects.
 */

declare global {
    interface Window {
        ReactNativeWebView?: { postMessage: (data: string) => void };
        __zbReceive?: (raw: string) => void;
    }
}

export const isInReactNativeWebView = (): boolean =>
    typeof window !== 'undefined' && !!window.ReactNativeWebView;

type WebHandler<K extends keyof WebApi> = (
    payload: WebApi[K]['req']
) => WebApi[K]['res'] | Promise<WebApi[K]['res']>;

type Pending = {
    resolve: (v: unknown) => void;
    reject: (e: Error) => void;
    timer: ReturnType<typeof setTimeout> | null;
};

const pending = new Map<string, Pending>();
const handlers = new Map<keyof WebApi, WebHandler<keyof WebApi>>();

const sendToNative = (msg: Envelope) => {
    if (typeof window === 'undefined' || !window.ReactNativeWebView) return;
    window.ReactNativeWebView.postMessage(JSON.stringify(msg));
};

const dispose = (id: string) => {
    const p = pending.get(id);
    if (!p) return;
    if (p.timer) clearTimeout(p.timer);
    pending.delete(id);
};

const handleIncoming = (raw: string) => {
    const msg = parseEnvelope(raw);
    if (!msg) return;

    if (msg.dir === 'res') {
        const p = pending.get(msg.id);
        if (!p) return;
        dispose(msg.id);
        p.resolve(msg.payload);
        return;
    }

    if (msg.dir === 'err') {
        const p = pending.get(msg.id);
        if (!p) return;
        dispose(msg.id);
        p.reject(new Error(msg.error.message));
        return;
    }

    // dir === 'req': native called callWeb(method, payload).
    void (async () => {
        const handler = handlers.get(msg.method as keyof WebApi);
        if (!handler) {
            sendToNative({
                __zb: BRIDGE_VERSION,
                id: msg.id,
                dir: 'err',
                error: { message: `no web handler: ${msg.method}` },
            });
            return;
        }
        try {
            const result = await handler(msg.payload as never);
            sendToNative({ __zb: BRIDGE_VERSION, id: msg.id, dir: 'res', payload: result });
        } catch (e) {
            const message = e instanceof Error ? e.message : String(e);
            sendToNative({
                __zb: BRIDGE_VERSION,
                id: msg.id,
                dir: 'err',
                error: { message },
            });
        }
    })();
};

if (typeof window !== 'undefined' && !window.__zbReceive) {
    window.__zbReceive = handleIncoming;
}

/** Default timeout for `callNative` (ms). Override via the per-call options. */
export const DEFAULT_CALL_NATIVE_TIMEOUT_MS = 30_000;

export type CallNativeOptions = { timeoutMs?: number };

export const callNative = <K extends keyof NativeApi>(
    method: K,
    payload: NativeApi[K]['req'],
    options?: CallNativeOptions
): Promise<NativeApi[K]['res']> => {
    if (!isInReactNativeWebView()) {
        return Promise.reject(
            new Error(`callNative('${String(method)}') called outside react-native webview`)
        );
    }
    return new Promise<NativeApi[K]['res']>((resolve, reject) => {
        const id = nextRequestId();
        const timeoutMs = options?.timeoutMs ?? DEFAULT_CALL_NATIVE_TIMEOUT_MS;
        const timer =
            timeoutMs > 0
                ? setTimeout(() => {
                    dispose(id);
                    reject(new Error(`bridge timeout: callNative('${String(method)}')`));
                }, timeoutMs)
                : null;
        pending.set(id, {
            resolve: resolve as (v: unknown) => void,
            reject,
            timer,
        });
        sendToNative({
            __zb: BRIDGE_VERSION,
            id,
            dir: 'req',
            method: method as string,
            payload,
        });
    });
};

/**
 * Register a handler for a method native may call. Returns an `unregister`
 * function. Re-registering replaces the previous handler for that method.
 */
export const registerWebHandler = <K extends keyof WebApi>(
    method: K,
    handler: WebHandler<K>
): (() => void) => {
    handlers.set(method, handler as unknown as WebHandler<keyof WebApi>);
    return () => {
        if (handlers.get(method) === (handler as unknown as WebHandler<keyof WebApi>)) {
            handlers.delete(method);
        }
    };
};
